import { StripeProductType } from '../stripe';

/**
 * Custom fee structure for account-specific fees
 */
export interface CustomFee {
  id: string;
  amountCents: number;
  description: string;
  isRecurring: boolean;
  enabled: boolean;
  createdAt: string;
}

export interface AccountPlan {
  accountId: string;
  plan: 'free' | 'scale' | 'subclub';
  stripeCustomerId?: string | undefined | null;
  billingEmail?: string | undefined | null;
  amountPlan: number;
  amountPerSuccessTransaction: number;
  amountPerSubclubTransaction: number;
  amountPerDeclinedTransaction: number;
  amountPer3dsVerification: number;
  amountPerChargeBack: number;
  amountPerRefund: number;
  amountPerLatePayment: number;
  // Enhanced pricing amount fields
  amountPerApplePayDomain: number;
  amountPerCustomDomain: number;
  // Percentage fields (basis points)
  pctPerSuccessTransaction: number;
  pctPerSubclubTransaction: number;
  pctPerDeclinedTransaction: number;
  pctPer3dsVerification: number;
  pctPerChargeBack: number;
  pctPerRefund: number;
  pctPerLatePayment: number;
  // Enhanced pricing percentage fields (basis points)
  pctPerAutomaticRetry: number;
  pctPerUpsell: number;
  pctPerSubscription: number;
  pctPerApplePay: number;
  pctPerGooglePay: number;
  /** TagadaRx CRM fee on Tagada Rx transactions (bps, default 390). */
  pctPerRxTransaction?: number;
  enableChargeForSuccessTransaction: boolean;
  enableChargeForSubclubTransaction: boolean;
  enableChargeForDeclinedTransaction: boolean;
  enableChargeFor3dsVerification: boolean;
  enableChargeForChargeBack: boolean;
  enableChargeForRefund: boolean;
  enableChargeForLatePayment: boolean;
  // Enhanced pricing enable fields
  enableChargeForAutomaticRetry: boolean;
  enableChargeForUpsell: boolean;
  enableChargeForSubscription: boolean;
  enableChargeForApplePay: boolean;
  enableChargeForGooglePay: boolean;
  enableChargeForApplePayDomain: boolean;
  enableChargeForCustomDomain: boolean;
  billingCurrency: string;
  currentPeriodStart?: Date | undefined | null;
  currentPeriodEnd?: Date | undefined | null;
  cancelAt?: Date;
  items?: AccountPlanItems;
  customFees?: CustomFee[];
}

export interface AccountPlanItems {
  url: string;
  data: any[];
  object: string;
  has_more: boolean;
  total_count: number;
}

export type AccountPlanPricesColumn =
  | 'amountPerSuccessTransaction'
  | 'amountPerSubclubTransaction'
  | 'amountPerDeclinedTransaction'
  | 'amountPerRefund'
  | 'amountPer3dsVerification'
  | 'amountPerChargeBack'
  | 'amountPerLatePayment'
  | 'amountPerApplePayDomain'
  | 'amountPerCustomDomain';

export type AccountPlanPercentageColumn =
  | 'pctPerSuccessTransaction'
  | 'pctPerSubclubTransaction'
  | 'pctPerDeclinedTransaction'
  | 'pctPerRefund'
  | 'pctPer3dsVerification'
  | 'pctPerChargeBack'
  | 'pctPerLatePayment'
  | 'pctPerAutomaticRetry'
  | 'pctPerUpsell'
  | 'pctPerSubscription'
  | 'pctPerApplePay'
  | 'pctPerGooglePay';

export type EnableChargeField =
  | 'enableChargeForSuccessTransaction'
  | 'enableChargeForSubclubTransaction'
  | 'enableChargeForDeclinedTransaction'
  | 'enableChargeFor3dsVerification'
  | 'enableChargeForChargeBack'
  | 'enableChargeForRefund'
  | 'enableChargeForLatePayment'
  | 'enableChargeForAutomaticRetry'
  | 'enableChargeForUpsell'
  | 'enableChargeForSubscription'
  | 'enableChargeForApplePay'
  | 'enableChargeForGooglePay'
  | 'enableChargeForApplePayDomain'
  | 'enableChargeForCustomDomain';

export const productTypeToEnableChargeField: Record<StripeProductType, EnableChargeField> = {
  approved_transaction: 'enableChargeForSuccessTransaction',
  approved_subclub_transaction: 'enableChargeForSubclubTransaction',
  declined_transaction: 'enableChargeForDeclinedTransaction',
  '3ds_verification': 'enableChargeFor3dsVerification',
  chargeback_alert: 'enableChargeForChargeBack',
  refund_processing: 'enableChargeForRefund',
  late_payment: 'enableChargeForLatePayment',
  // Enhanced pricing categories
  automatic_retry: 'enableChargeForAutomaticRetry',
  upsell: 'enableChargeForUpsell',
  subscription: 'enableChargeForSubscription',
  apple_pay: 'enableChargeForApplePay',
  google_pay: 'enableChargeForGooglePay',
  apple_pay_domain: 'enableChargeForApplePayDomain',
  custom_domain: 'enableChargeForCustomDomain',
};

export const productTypeToPercentageField: Record<StripeProductType, AccountPlanPercentageColumn> = {
  approved_transaction: 'pctPerSuccessTransaction',
  approved_subclub_transaction: 'pctPerSubclubTransaction',
  declined_transaction: 'pctPerDeclinedTransaction',
  '3ds_verification': 'pctPer3dsVerification',
  chargeback_alert: 'pctPerChargeBack',
  refund_processing: 'pctPerRefund',
  late_payment: 'pctPerLatePayment',
  // Enhanced pricing categories
  automatic_retry: 'pctPerAutomaticRetry',
  upsell: 'pctPerUpsell',
  subscription: 'pctPerSubscription',
  apple_pay: 'pctPerApplePay',
  google_pay: 'pctPerGooglePay',
  // Note: apple_pay_domain and custom_domain use fixed pricing, not percentage
  apple_pay_domain: 'pctPerSuccessTransaction', // Placeholder - not actually used
  custom_domain: 'pctPerSuccessTransaction', // Placeholder - not actually used
};
